<template>
    <div class="vue-nexdrive-finder nexdrive-finder" :class="{'map-active': isMobile && showMap, 'list-active': isMobile && !showMap }">
        <div class="finder-wrapper">
            <div class="search-bar-wrapper">
                <div class="row">
                    <div class="search-bar col-12 col-lg-4">
                        <div class="inner">
                            <h1 class="virtual-h3 mb-2">
                                <span class="font-weight-normal">{{ title }}</span> {{ subtitle }}
                            </h1>

                            <div :style="{'visibility': (hasActiveMarker ? 'hidden' : 'initial')}">
                                <div class="finder-search-input">
                                    <div class="input-group finder-input">
                                        <gmap-autocomplete
                                            ref="gmapAutocomplete"
                                            class="vue-gmap-autocomplete form-control"
                                            :placeholder="gmapPlaceholderTxt"
                                            :select-first-on-enter="true"
                                            :options="autocompleteOptions"
                                            @focus="loadMap"
                                            @place_changed="setPlace">
                                        </gmap-autocomplete>

                                        <button
                                            v-if="showResetButton"
                                            @click="handleReset"
                                            class="reset-map"
                                        ><span class="visually-hidden">Reset map</span></button>
                                    </div>
                                </div>

                                <template v-if="isMobile">
                                    <div class="mobile-toggle">
                                        <button :class="{'active': !showMap}" @click="showMap = false">{{ listViewText }}</button>
                                        <button :class="{'active': showMap}" @click="showMap = true" aria-hidden="true">{{ mapViewText }}</button>
                                    </div>
                                </template>

                                <template v-if="hasMarkers && !loading">
                                    <perfect-scrollbar>
                                        <div class="marker-meta">
                                            <p aria-live="polite"> {{ markersLength }} {{ locationText }} <template v-if="hasPlace">{{ closeToYouText }}</template></p>
                                        </div>

                                        <div class="results-wrapper" v-if="showList">
                                            <nd-marker-mini-card
                                                v-for="(marker, index) in computedMarkers"
                                                :key="index"
                                                :marker="marker"
                                                :event-bus="eventBus"
                                                @marker-clicked="handleMarkerClicked"
                                                @active-marker="handleActiveMarker"
                                                :domain-id="domainOptions.domainId"
                                            ></nd-marker-mini-card>
                                        </div>
                                    </perfect-scrollbar>
                                </template>
                            </div>

                            <template v-if="loading">
                                <div class="loader"><span class="visually-hidden">{{ loadingText }}</span></div>
                            </template>

                            <transition name="fade" v-if="!isMobile">
                                <nd-active-marker
                                    v-if="hasActiveMarker"
                                    :marker="activeMarker"
                                    :event-bus="eventBus"
                                    :domain-id="domainOptions.domainId"
                                    :country-code="domainOptions.countryCode"
                                ></nd-active-marker>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>

            <template v-if="doMapLoad === true">
                <div class="store-locator" v-show="showMap" aria-hidden="true">
                    <GmapMap
                        :center="center"
                        ref="mapRef"
                        :zoom="mapZoom"
                        map-type-id="roadmap"
                        :options="mapOptions"
                        @zoom_changed="mapZoom = $event"
                    >
                        <gmap-cluster
                            :zoom-on-click="true"
                        >
                            <gmap-custom-marker
                                v-for="(marker, index) in markers"
                                :marker="{lat: marker.geolocation.lat, lng: marker.geolocation.lng}"
                                :key="index"
                                class="gmap-custom-marker"
                                :class="activeMarker === marker ? 'active' : 'inactive'"
                            >
                                <marker-wrapper
                                    :marker="marker"
                                    :event-bus="eventBus"
                                    :active-marker="activeMarker"
                                    @marker-clicked="handleMarkerClicked"
                                    @active-marker="handleActiveMarker"
                                ></marker-wrapper>
                            </gmap-custom-marker>
                        </gmap-cluster>
                    </GmapMap>
                </div>
            </template>

            <transition name="fade" v-if="isMobile">
                <nd-active-marker
                    v-if="hasActiveMarker"
                    :marker="activeMarker"
                    :event-bus="eventBus"
                    :domain-id="domainOptions.domainId"
                    :country-code="domainOptions.countryCode"
                ></nd-active-marker>
            </transition>
        </div>
    </div>
</template>

<script>
import {mixin as focusMixin} from './../../../../../themes/custom/ef2_custom/node_modules/vue-focus';
import GmapCustomMarker from './../../../../../themes/custom/ef2_custom/node_modules/vue2-gmap-custom-marker';
import GmapCluster from './../../../../../themes/custom/ef2_custom/node_modules/vue2-google-maps/dist/components/cluster';
import MarkerWrapper from '../../../../../modules/custom/nexdrive_base/js/components/MarkerWrapper';
import MarkerMiniCard from "./MarkerMiniCard";
import ActiveMarker from "./ActiveMarker";

export default {
    name: "NexdriveFinder",

    mixins: [focusMixin],

    components: {
        'gmap-custom-marker': GmapCustomMarker,
        'gmap-cluster': GmapCluster,
        'marker-wrapper': MarkerWrapper,
        'nd-marker-mini-card': MarkerMiniCard,
        'nd-active-marker': ActiveMarker,
    },

    props: {
        bundle: {
            type: String,
            default: ""
        },
        domainOptions: {}
    },

    data() {
        return {
            eventBus: new Vue(),
            markers: [],
            doMapLoad: false,
            showMap: true,

            title: window.Drupal.t('Find your'),
            subtitle: window.Drupal.t('location'),
            gmapPlaceholderTxt: window.Drupal.t('Search by city or Postal Code'),
            locationText: window.Drupal.t('locations'),
            closeToYouText: window.Drupal.t('close to you'),
            loadingText: window.Drupal.t('Loading...'),
            listViewText: window.Drupal.t('List view'),
            mapViewText: window.Drupal.t('Map view'),

            activeMarker: {},

            mapOptions: {
                streetViewControl: false,
                fullscreenControl: false,

                styles: [
                    {
                        "featureType": "all",
                        "elementType": "labels.text",
                        "stylers": [
                            {
                                "color": "#ff0000"
                            }
                        ]
                    },
                    {
                        "featureType": "all",
                        "elementType": "labels.text.fill",
                        "stylers": [
                            {
                                "saturation": 36
                            },
                            {
                                "color": "#ffffff"
                            },
                            {
                                "lightness": 40
                            }
                        ]
                    },
                    {
                        "featureType": "all",
                        "elementType": "labels.text.stroke",
                        "stylers": [
                            {
                                "visibility": "on"
                            },
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 16
                            }
                        ]
                    },
                    {
                        "featureType": "all",
                        "elementType": "labels.icon",
                        "stylers": [
                            {
                                "visibility": "off"
                            }
                        ]
                    },
                    {
                        "featureType": "administrative",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 20
                            }
                        ]
                    },
                    {
                        "featureType": "administrative",
                        "elementType": "geometry.stroke",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 17
                            },
                            {
                                "weight": 1.2
                            }
                        ]
                    },
                    {
                        "featureType": "landscape",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 20
                            }
                        ]
                    },
                    {
                        "featureType": "landscape",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "color": "#1C2B4A"
                            }
                        ]
                    },
                    {
                        "featureType": "landscape",
                        "elementType": "labels.text.fill",
                        "stylers": [
                            {
                                "color": "#ffffff"
                            }
                        ]
                    },
                    {
                        "featureType": "poi",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 21
                            }
                        ]
                    },
                    {
                        "featureType": "poi.park",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "color": "#080707"
                            }
                        ]
                    },
                    {
                        "featureType": "road",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "weight": "0.48"
                            }
                        ]
                    },
                    {
                        "featureType": "road.highway",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "color": "#3cf000"
                            },
                            {
                                "lightness": 17
                            }
                        ]
                    },
                    {
                        "featureType": "road.highway",
                        "elementType": "geometry.stroke",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 29
                            },
                            {
                                "weight": 0.2
                            }
                        ]
                    },
                    {
                        "featureType": "road.arterial",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 18
                            }
                        ]
                    },
                    {
                        "featureType": "road.arterial",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "saturation": "0"
                            },
                            {
                                "lightness": "-100"
                            },
                            {
                                "gamma": "1.31"
                            }
                        ]
                    },
                    {
                        "featureType": "road.arterial",
                        "elementType": "geometry.stroke",
                        "stylers": [
                            {
                                "color": "#ff0000"
                            }
                        ]
                    },
                    {
                        "featureType": "road.local",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 16
                            }
                        ]
                    },
                    {
                        "featureType": "road.local",
                        "elementType": "geometry.fill",
                        "stylers": [
                            {
                                "color": "#000000"
                            }
                        ]
                    },
                    {
                        "featureType": "transit",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#000000"
                            },
                            {
                                "lightness": 19
                            }
                        ]
                    },
                    {
                        "featureType": "water",
                        "elementType": "geometry",
                        "stylers": [
                            {
                                "color": "#0f252e"
                            },
                            {
                                "lightness": 17
                            }
                        ]
                    }
                ]
            },

            place: null,
            center: {lat: 62.966070, lng: -96.662775},
            mapZoom: 4,

            autocompleteOptions: {
                types: ['geocode'],
                componentRestrictions: {
                    country: ['ca']
                }
            },

            filteredMarkers: [],
            gmapInputValue: '',
            loading: false,
        }
    },

    mounted() {
        this.mapZoom = this.domainOptions.zoomLevel;
        this.autocompleteOptions.componentRestrictions.country = this.domainOptions.focusRegion;
        this.center = this.domainOptions.geolocation;

        this.checkDevice();
        this.fetchData();
        setTimeout(() => this.doMapLoad = true, 1000);
        this.eventBus.$on('remove-active-marker', () => this.handleRemoveActiveMarker());
    },

    computed: {
        device() {
            return this.$mq;
        },

        isMobile() {
            return this.device === 'mobile';
        },

        showList() {
            if (!this.isMobile) {
                return true;
            }

            return !this.showMap;
        },

        hasActiveMarker() {
            return !_.isEmpty(this.activeMarker);
        },

        hasMarkers() {
            return ! _.isEmpty(this.markers)
        },

        hasPlace() {
            return ! _.isNil(this.place);
        },

        markersLength() {
            return Object.keys(this.computedMarkers).length;
        },

        showFilteredMarkers() {
            return !_.isEmpty(this.filteredMarkers) && this.hasPlace && !this.loading;
        },

        noFilteredMarkersResult() {
            return _.isEmpty(this.filteredMarkers) && !_.isEmpty(this.gmapInputValue) && !this.loading;
        },

        showResetButton() {
            return ! _.isEmpty(this.gmapInputValue) && !this.loading;
        },

        computedMarkers() {
            if (this.showFilteredMarkers) {
                return this.filteredMarkers;
            }

            return this.markers;
        },
    },

    methods: {
        fetchData(fromPlace = false) {
            this.loading = true;
            let url = `/${window.drupalSettings.ef2_custom.lang_code}/api/garages`;

            if (fromPlace) {
                url = url.concat(`?lat=${this.place.geometry.location.lat()}&lng=${this.place.geometry.location.lng()}`);
            }

            axios.get(url)
                .then(response => {
                    if (!fromPlace) {
                        this.markers = response.data;
                        return;
                    }

                    this.filteredMarkers = response.data;
                })
                .catch(error => {
                    this.markers = [];
                    this.filteredMarkers = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        loadMap() {
            setTimeout(this.doMapLoad = true, 500);
        },

        setPlace(place) {
            if (_.isEmpty(place.name)) {
                this.handleReset();
                return;
            }

            this.place = place
            this.goToPlace(place);
            this.gmapInputValue = this.$refs.gmapAutocomplete.$refs.input.value;
            this.fetchData(true);
        },

        checkBundleType() {
            if (this.bundle !== "vestiging_overzicht") {
                this.doMapLoad = false;
                return;
            }

            this.doMapLoad = true;
        },

        goToPlace() {
            if (!this.place || _.isNil(this.place.geometry)) {
                // this.$toaster.error('Er is geen locatie gevonden. ')
                return;
            }

            this.center.lat = this.place.geometry.location.lat();
            this.center.lng = this.place.geometry.location.lng();

            this.mapZoom = 9;

            this.title = window.Drupal.t('You have');
            this.subtitle = window.Drupal.t('searched for');
        },

        geoLocate() {
            navigator.geolocation.getCurrentPosition(position => {
                    this.center = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };

                    this.mapZoom = 11;

                    // Mag uit voor prod
                    //this.$toaster.success('Locatie succesvol opgehaald!')
                });
        },

        handleMarkerClicked(payload) {
            this.mapZoom = 11;

            let latOffset = {
                11: 0.07,
            };

            let lngOffset = {
                11: 0.100,
            };

            if (this.device === 'mobile') {
                latOffset = {
                    11: 0,
                }

                lngOffset = {
                    11: 0,
                }
            }

            let calculatedLat = parseFloat(payload.lat) - (latOffset[this.mapZoom] ?? 0);
            let calculatedLng = parseFloat(payload.lng) - (lngOffset[this.mapZoom] ?? 0);

            this.$refs.mapRef.panTo({lat: calculatedLat, lng: calculatedLng});
            //this.$refs.mapRef.panTo({lat: parseFloat(payload.lat), lng: parseFloat(payload.lng)})
        },

        handleActiveMarker(payload) {
            if (_.isEmpty(payload)) {
                this.mapZoom = 4;
                this.$refs.mapRef.panTo(this.center);
            }

            this.activeMarker = payload;
        },

        handleRemoveActiveMarker() {
            this.activeMarker = {};
            let zoom = 4;

            if (!_.isEmpty(this.gmapInputValue)) {
                zoom = 11;
            }

            this.mapZoom = zoom;
            this.$refs.mapRef.panTo(this.center);
        },

        handleReset() {
            this.title = window.Drupal.t('Find your'),
            this.subtitle = window.Drupal.t('location'),
            this.filteredMarkers = {};
            this.place = null;
            this.gmapInputValue = '';
            this.$refs.gmapAutocomplete.$refs.input.value = '';
            this.mapZoom = 4;
            this.center = this.domainOptions.geolocation;
            this.$refs.mapRef.panTo(this.center);
        },

        checkDevice() {
            if (this.device !== 'mobile') {
                return;
            }

            this.showMap = false;
        }
    }
}
</script>
